import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ApiService,
  FormTypeEnum,
  SystemFormExtraActionsEnum,
  ViewTypeEnum,
} from "../../api";
import { Button, Col, Collapse, Row, Space, Steps } from "antd";
import { StepProps } from "antd/lib";
import { CreateStepType } from "../../constants";
import WorkflowViewPage from "./WorkflowViewPage";
import RenderFormComponent from "../../components/RenderFormComponent";
import { CaretRightOutlined } from "@ant-design/icons";
import { CreateStepsProps, ReadOnlySectionHint } from "./EditRequestPage";
import { useCloseOwnTab } from "../../tabs/tabContext";

export type CreateFormsProps = {
  form_id?: number;
  schema?: any;
  form?: any;
  workflow_id?: number;
  type: CreateStepType;
  formType?: FormTypeEnum;
  system_form_extra_actions?: any;
  // Whether the current user may fill this section right now (false -> read-only).
  editable_now?: boolean;
  fill_group_name?: string | null;
  // Which gate is holding it: 'not_owner' | 'stage_not_open' | 'not_applicable'
  // | null.
  editable_reason?: string | null;
  // The stage this section belongs to, for a 'stage_not_open' hint.
  stage_name?: string | null;
  // False when this section's own rule rules it out for this case. Always true
  // on the create path: there is no request yet, so nothing to evaluate against.
  applies_now?: boolean;
  // The rule in words, so the hint can say why rather than only that.
  condition_text?: string;
} & StepProps;

const CreateRequestPage = () => {
  const { t } = useTranslation();
  const { shortName } = useParams();
  const { state } = useLocation();

  const location = useNavigate();
  const closeOwnTab = useCloseOwnTab();
  const [viewType, setViewType] = useState<ViewTypeEnum>(ViewTypeEnum.COLLAPSE);
  const [current, setCurrent] = useState<number>(0);
  const [name, setName] = useState("");
  const [items, setItems] = useState<CreateFormsProps[]>([]);
  const [requestId, setRequestId] = useState<number | undefined>(undefined);
  const [duplicateRequestId, setDuplicateRequestId] = useState<
    number | undefined
  >(undefined);

  const prev = () => {
    setCurrent(current - 1);
  };

  /**
   * Sections + approval steps from a template payload, or from the `template`
   * of a request payload -- the same shape, and the flags below are the reason
   * it matters which one we read.
   */
  const mapItems = (template: any): CreateFormsProps[] => {
    const forms: CreateFormsProps[] = (template.form_templates ?? []).map(
      (ft: any, index: number) => ({
        key: index,
        title: t(ft.title),
        form_id: ft.id,
        schema: ft.schema_json.schema,
        form: ft.schema_json.form,
        type: CreateStepType.Form,
        formType: ft.type,
        system_form_extra_actions: ft.system_form_extra_actions,
        editable_now: ft.editable_now,
        fill_group_name: ft.fill_group_name,
        editable_reason: ft.editable_reason,
        stage_name: ft.stage_name,
        applies_now: (ft as any).applies_now,
        condition_text: (ft as any).condition_text,
      })
    );
    const workflows: CreateFormsProps[] = (template.workflows ?? []).map(
      (wf: any, index: number) => ({
        key: index,
        title: t(wf.name),
        workflow_id: wf.id,
        type: CreateStepType.Workflow,
      })
    );
    return [...forms, ...workflows];
  };

  /**
   * Re-read the sections now that the request exists.
   *
   * Until the first save there is no request, so the server answers
   * `editable_now: true` for everything -- it has no stage context to judge
   * against. Keeping that first answer for the rest of the wizard is what put a
   * live Save button on a section the server would refuse: the request is real
   * now, and only the request knows which stage it sits on and who owns what.
   */
  const refreshSections = async (id: number) => {
    try {
      const result = await ApiService.apiMainRequestsRetrieve(id);
      setItems(mapItems(result.template));
    } catch (e) {
      // A stale flag is a worse screen, not a broken one -- the server still
      // refuses anything it should, and now says so out loud.
    }
  };

  const sendForReview = async () => {
    await ApiService.apiMainGenerateDocumentRetrieve(requestId!);
    // The request is created; close this Create tab before navigating so the
    // user is left on the request itself rather than an empty create form.
    closeOwnTab();
    location("/requests/" + requestId);
  };

  const handleSuccessSubmit = async (result: any) => {
    setRequestId(result.request_id);
    if (result.request_id) await refreshSections(result.request_id);
    if (current === items.length - 1) {
      sendForReview();
    } else {
      setCurrent(current + 1);
    }
  };

  useEffect(() => {
    if (state !== null) {
      setDuplicateRequestId(state.requestId);
      (async () => {
        await ApiService.apiMainRequestsRetrieve(
          parseInt(state.requestId!)
        ).then((result) => {
          setViewType(result.template.view_type ?? ViewTypeEnum.COLLAPSE);
          setItems(mapItems(result.template));
        });
      })();
    } else {
      (async () => {
        await ApiService.apiMainTemplatesRetrieve(shortName!).then((result) => {
          setViewType(result.view_type ?? ViewTypeEnum.COLLAPSE);
          setName(result.name);
          setItems(mapItems(result));
        });
      })();
    }
  }, []);

  const renderView = () => {
    if (items.length === 0) {
      return null;
    }

    if (items[current].type === CreateStepType.Form) {
      const editable = items[current].editable_now !== false;
      return (
        <>
          {!editable && (
            <ReadOnlySectionHint
              fillGroupName={items[current].fill_group_name}
              reason={items[current].editable_reason}
              stageName={items[current].stage_name}
              conditionText={items[current].condition_text}
            />
          )}
          <RenderFormComponent
            form={items[current]}
            requestId={requestId}
            readonly={!editable}
            onSuccess={handleSuccessSubmit}
            duplicateRequestId={duplicateRequestId}
            setDuplicateRequestId={setDuplicateRequestId}
            showPrev={current > 0}
            onPrev={prev}
          />
          {!editable && (
            <Row justify="end" style={{ marginTop: 12 }}>
              <Button
                type="primary"
                onClick={() => {
                  // Read-only step has no Save to advance the wizard; a section
                  // owner fills it later. Just move on (or finish if last).
                  if (current === items.length - 1) sendForReview();
                  else setCurrent(current + 1);
                }}
              >
                {t("Next")}
              </Button>
            </Row>
          )}
        </>
      );
    }

    return (
      <WorkflowViewPage
        requestId={requestId}
        workflowId={items[current].workflow_id}
        onSuccess={handleSuccessSubmit}
        showPrev={current > 0}
        onPrev={prev}
      />
    );
  };

  if (viewType === ViewTypeEnum.STEP) {
    return (
      <div>
        <h4>{t("Create {{name}}", { name })}</h4>
        <Steps current={current} items={items} onChange={setCurrent} />
        <div style={{ marginTop: 24 }}>{renderView()}</div>
      </div>
    );
  }

  return (
    <div>
      <h4>{t("Create {{name}}", { name })}</h4>
      <Space direction="vertical" style={{ width: "100%" }}>
        {items.map((page) => (
          <Collapse
            expandIcon={({ isActive }) => (
              <CaretRightOutlined rotate={isActive ? 90 : 0} />
            )}
            ghost={true}
            items={[
              {
                key: page.form_id,
                label: page.title,
                children:
                  page.type === CreateStepType.Form ? (
                    <>
                      {page.editable_now === false && (
                        <ReadOnlySectionHint
                          fillGroupName={page.fill_group_name}
                          reason={page.editable_reason}
                          stageName={page.stage_name}
                          conditionText={page.condition_text}
                        />
                      )}
                      <RenderFormComponent
                        form={page}
                        requestId={requestId}
                        readonly={page.editable_now === false}
                        onSuccess={handleSuccessSubmit}
                        duplicateRequestId={duplicateRequestId}
                        setDuplicateRequestId={setDuplicateRequestId}
                      />
                    </>
                  ) : (
                    <WorkflowViewPage
                      requestId={requestId}
                      workflowId={page.workflow_id}
                      onSuccess={handleSuccessSubmit}
                      duplicateRequestId={duplicateRequestId}
                      setDuplicateRequestId={setDuplicateRequestId}
                    />
                  ),
              },
            ]}
          />
        ))}
      </Space>
      <Col span={24} style={{ textAlign: "center", marginTop: 24 }}>
        <Button
          style={{ margin: "auto" }}
          type="primary"
          onClick={() => sendForReview()}
        >
          {t("Review")}
        </Button>
      </Col>
    </div>
  );
};

export default CreateRequestPage;
